// ---------------------------------------------------------------------------
// The audience seat, and how it is drawn so nobody mistakes it for a person.
//
// `settings.audienceSeat` folds the whole spectator bench into ONE extra seat:
// it has a name, it has a score, it appears in the running order and in the
// standing, and it never chooses a board. Everything a screen needs to know
// about it is here, because the alternative is four files each deciding for
// themselves what an audience row looks like and one of them deciding it looks
// like a player.
//
// **Two facts have to be on the row and both of them are easy to leave off.**
//
// It is not a person. A row that carries a display name, a score and nothing
// else is a row about somebody, and a viewer scanning a standing has no way to
// tell that this one is two hundred people's answers put through
// `settings.audienceAggregation`. So the mark is text — `the audience` — rather
// than a colour or an icon: colour is what the room stopped being identified by
// at `COLORED_SEATS`, and an icon is a thing to learn.
//
// And it may not be able to win. With `audienceCanWin` off the seat is scored
// and shown and then skipped when the winner is worked out, which is a rule a
// player finding it above them in the standing is owed an explanation of at the
// moment they find it — not on the end screen, where the seat they were chasing
// silently stops being the leader.
//
// **Recognising it is one predicate and it lives here**, over the id that
// `@countdown/shared` exports. It used to be a local copy of the string, with a
// note saying shared did not name it — shared did, by the time that note was
// written, so it was two spellings of one id waiting to disagree.
//
// **This does not decide whether the seat EXISTS.** That is
// `audienceSeatAppears` in shared, asked once on the server, and the reason a
// room with the setting on but nobody watching sends no such player at all.
// Everything here answers "given a player, is it the audience" and takes the
// payload's word for whether there is one.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import { AUDIENCE_SEAT_ID, type ClientGameState, type PublicPlayer } from '@countdown/shared';

/** Is this seat the audience rather than a person? */
export function isAudienceSeat(player: PublicPlayer): boolean {
  return player.id === AUDIENCE_SEAT_ID;
}

/** Is this seat shown and scored but skipped when the winner is worked out? */
export function audienceIsBarredFromWinning(state: ClientGameState, player: PublicPlayer): boolean {
  return isAudienceSeat(player) && state.settings.audienceCanWin === false;
}

/**
 * The tag that goes beside the audience's name, or nothing for a person.
 *
 * A chip rather than a word in the name: a display name is chosen by whoever is
 * wearing it, and a suffix inside one would be a claim anybody could make about
 * themselves. This is structurally outside the name for the same reason
 * `PlayerChip`'s handle slot is.
 *
 * `not for the win` only when the setting is actually off. A room that left
 * `audienceCanWin` on has an audience that is simply another competitor, and a
 * caveat about a rule that is not in force is noise on every row of every round.
 */
export function AudienceTag({
  state,
  player,
}: {
  state: ClientGameState;
  player: PublicPlayer;
}): ReactElement | null {
  if (!isAudienceSeat(player)) return null;
  const barred = audienceIsBarredFromWinning(state, player);

  return (
    <span className="flex shrink-0 items-center gap-1" data-audience-seat="">
      <span className="rounded-chip border border-line px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap text-ink-dim uppercase">
        the audience
      </span>
      {barred ? (
        <span className="rounded-chip border border-line px-1.5 py-px font-mono text-[10px] font-semibold tracking-[0.08em] whitespace-nowrap text-ink-dim uppercase">
          not for the win
        </span>
      ) : null}
    </span>
  );
}
